import {
  doc,
  updateDoc,
} from "@/services/legacyBrowserPersistenceBoundary";
import { storageAuthorityService } from "@/services/storageAuthorityService";

const LOGO_EXPIRY_SKEW_MS = 60_000;
const logoDownloadCache = new Map();
let logoCacheGeneration = 0;

const clearLogoDownloadCache = () => {
  logoCacheGeneration += 1;
  logoDownloadCache.clear();
};

const loadLogoDownloadUrl = async (assetId) => {
  const now = Date.now();
  const cached = logoDownloadCache.get(assetId);
  if (cached?.downloadUrl && cached.expiresAt - LOGO_EXPIRY_SKEW_MS > now) {
    return cached.downloadUrl;
  }
  if (cached?.promise) return cached.promise;
  const requestGeneration = logoCacheGeneration;
  const promise = storageAuthorityService.createAssetDownloadUrl(assetId)
    .then((result) => {
      if (
        logoCacheGeneration === requestGeneration &&
        logoDownloadCache.get(assetId)?.promise === promise
      ) {
        logoDownloadCache.set(assetId, result);
      }
      return result.downloadUrl;
    })
    .catch((error) => {
      if (logoDownloadCache.get(assetId)?.promise === promise) {
        logoDownloadCache.delete(assetId);
      }
      throw error;
    });
  logoDownloadCache.set(assetId, { promise });
  return promise;
};

const activeUserUID = (rootGetters) =>
  rootGetters?.userCredentials?.data?.uid ||
  rootGetters?.user?.data?.uid ||
  rootGetters?.user?.uid ||
  null;

const ownsActiveLogoContext = (state, rootGetters) =>
  !!state.logoOwnerUID &&
  !!state.logoOrgUID &&
  state.logoOwnerUID === activeUserUID(rootGetters) &&
  state.logoOwnerUID === rootGetters?.selectedOrgOwnerUID &&
  state.logoOrgUID === rootGetters?.selectedOrgUID;

export const coinSymbolStates = {
  state: () => ({
    // Currency is active-organisation state, not a global browser preference.
    // It is populated from the exact owner-scoped wallet context at runtime.
    coinSymbol: "",
    coinName: "",
    // Logo is an in-memory projection of the authenticated selected org.
    logoUrl: "",
    logoOwnerUID: null,
    logoOrgUID: null,
    logoRequestGeneration: 0,
    logoStatus: "idle",
  }),
  getters: {
    getCoinSymbol: (state) => state.coinSymbol,
    getCoinName: (state) => state.coinName,
    getLogoUrl: (state, _getters, _rootState, rootGetters) =>
      ownsActiveLogoContext(state, rootGetters) ? state.logoUrl || "" : "",
    getLogoStatus: (state, _getters, _rootState, rootGetters) =>
      ownsActiveLogoContext(state, rootGetters) ? state.logoStatus : "idle",
  },
  mutations: {
    setCoinSymbol(state, symbol) {
      state.coinSymbol = symbol;
    },
    setCoinName(state, name) {
      state.coinName = name;
    },
    beginLogoFetch(state, { uid, orgUID }) {
      state.logoRequestGeneration += 1;
      state.logoOwnerUID = uid;
      state.logoOrgUID = orgUID;
      state.logoUrl = "";
      state.logoStatus = "pending";
    },
    completeLogoFetch(
      state,
      { uid, orgUID, generation, logoUrl = "", status = "ready" }
    ) {
      if (
        state.logoOwnerUID !== uid ||
        state.logoOrgUID !== orgUID ||
        state.logoRequestGeneration !== generation
      ) {
        return;
      }
      state.logoUrl = logoUrl || "";
      state.logoStatus = status;
    },
    setLogoUrl(state, { uid, orgUID, logoUrl = "" } = {}) {
      if (state.logoOwnerUID !== uid || state.logoOrgUID !== orgUID) return;
      // A local save/removal supersedes any pending fetch for this context.
      state.logoRequestGeneration += 1;
      state.logoUrl = logoUrl || "";
      state.logoStatus = "ready";
    },
    clearLogoContext(state) {
      state.logoRequestGeneration += 1;
      state.logoOwnerUID = null;
      state.logoOrgUID = null;
      state.logoUrl = "";
      state.logoStatus = "idle";
    },
  },
  actions: {
    updateCoinSymbol({ commit }, { symbol, name }) {
      // console.log(
      //   "Action updateCoinSymbol called with symbol:",
      //   symbol,
      //   "name:",
      //   name
      // );
      if (name) {
        commit("setCoinName", name);
      }
      commit("setCoinSymbol", symbol);
    },
    async fetchLogoUrl({ commit, state, rootGetters }, { uid, orgUID } = {}) {
      if (
        !uid ||
        !orgUID ||
        activeUserUID(rootGetters) !== uid ||
        rootGetters.selectedOrgOwnerUID !== uid ||
        rootGetters.selectedOrgUID !== orgUID
      ) {
        commit("clearLogoContext");
        return null;
      }

      if (state.logoOwnerUID !== uid || state.logoOrgUID !== orgUID) {
        clearLogoDownloadCache();
      }
      commit("beginLogoFetch", { uid, orgUID });
      const generation = state.logoRequestGeneration;
      try {
        const assetId = rootGetters.activeSelectedOrg?.avatarAssetId || null;
        const logoUrl = assetId ? await loadLogoDownloadUrl(assetId) : "";
        commit("completeLogoFetch", { uid, orgUID, generation, logoUrl });
        return logoUrl;
      } catch (_error) {
        commit("completeLogoFetch", {
          uid,
          orgUID,
          generation,
          status: "error",
        });
        return null;
      }
    },
    async updateLogoUrl(
      { commit, state, rootGetters },
      { uid, orgUID, logoUrl } = {}
    ) {
      if (
        !uid ||
        !orgUID ||
        activeUserUID(rootGetters) !== uid ||
        rootGetters.selectedOrgOwnerUID !== uid ||
        rootGetters.selectedOrgUID !== orgUID
      ) {
        return false;
      }

      commit("beginLogoFetch", { uid, orgUID });
      const generation = state.logoRequestGeneration;
      try {
        const orgDocRef = doc(null, "custom-orgs", orgUID);
        await updateDoc(orgDocRef, { logo: logoUrl });
        commit("completeLogoFetch", {
          uid,
          orgUID,
          generation,
          logoUrl,
        });
        return true;
      } catch (error) {
        console.error("Error updating logo URL:", error);
        commit("completeLogoFetch", {
          uid,
          orgUID,
          generation,
          status: "error",
        });
        return false;
      }
    },
    setActiveLogoUrl({ commit, rootGetters }, { orgUID, logoUrl } = {}) {
      const uid = activeUserUID(rootGetters);
      if (
        !uid ||
        !orgUID ||
        rootGetters.selectedOrgOwnerUID !== uid ||
        rootGetters.selectedOrgUID !== orgUID
      ) {
        return false;
      }
      commit("setLogoUrl", { uid, orgUID, logoUrl });
      return true;
    },
    clearLogoContext({ commit }) {
      clearLogoDownloadCache();
      commit("clearLogoContext");
    },
  },
};
